import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import RuleAccountFilter, {
  EMPTY_RULE_ACCOUNT_FILTER,
  ruleFilterInput,
} from "@app/components/rule-account-filter";
import type { RuleAccountFilterValue } from "@app/components/rule-account-filter";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { useToast } from "@app/components/toast";
import UnprojectedDecisions from "./unprojected-decisions";
import TxnTablePanel, {
  BulkAction,
  useTxnSort,
} from "@app/components/txn-table-panel";
import type { TxnSortColumn } from "@app/components/txn-table-panel";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useMemo, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { needsReviewPage_Query } from "@app/queries/needsReviewPage_Query.graphql";
import type { needsReviewPage_MarkTxnsInternalMutation } from "@app/queries/needsReviewPage_MarkTxnsInternalMutation.graphql";
import type { needsReviewPage_AttributeTxnsMutation } from "@app/queries/needsReviewPage_AttributeTxnsMutation.graphql";
import type { needsReviewPage_SetTxnsNeedsReviewMutation } from "@app/queries/needsReviewPage_SetTxnsNeedsReviewMutation.graphql";
import type { needsReviewPage_CreateRuleMutation } from "@app/queries/needsReviewPage_CreateRuleMutation.graphql";
import type { needsReviewPage_TxnCounterpartLinkMutation } from "@app/queries/needsReviewPage_TxnCounterpartLinkMutation.graphql";

// Heavier txn rows with per-row candidate finders → 100 per page.
export const PAGE_SIZE = 100;

export const needsReviewQuery = graphql`
  query needsReviewPage_Query(
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
    $limit: Int
    $offset: Int
  ) {
    transactions(
      needsReview: true
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
      limit: $limit
      offset: $offset
    ) {
      totalCount
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        category
        needsReviewManual
        expenseNature {
          id
          name
        }
        counterparty {
          name
        }
        counterpartCandidateCount
        counterpartCandidates {
          txn {
            id
          }
          amount
          postingDate
          bankAccount {
            nickname
            last4
          }
        }
      }
    }
    bankAccounts {
      id
      name
      nickname
      last4
      number
    }
    allStakeholders {
      id
      name
    }
    unprojectedDecisions {
      id
      dimension
      valueLabel
      txnDescription
      txnPostingDate
      txnAmount
      decidedBy
    }
  }
`;

const markTxnsInternalMutation = graphql`
  mutation needsReviewPage_MarkTxnsInternalMutation($input: MarkTxnsInternalInputObject!) {
    markTxnsInternal(input: $input) {
      updatedCount
      destination {
        id
        name
        nickname
      }
      suggestion {
        regex
        matchKind
        additionalCount
        totalCount
      }
      errors {
        message
      }
    }
  }
`;

const createRuleMutation = graphql`
  mutation needsReviewPage_CreateRuleMutation($input: CreateRuleInputObject!) {
    createRule(input: $input) {
      rule {
        id
        matchesCount
      }
      errors {
        message
      }
    }
  }
`;

const attributeTxnsMutation = graphql`
  mutation needsReviewPage_AttributeTxnsMutation($input: AttributeTxnsInputObject!) {
    attributeTxns(input: $input) {
      stakeholder {
        id
        name
      }
      suggestion {
        regex
        matchKind
        additionalCount
        totalCount
      }
      errors {
        message
      }
    }
  }
`;

const setNeedsReviewMutation = graphql`
  mutation needsReviewPage_SetTxnsNeedsReviewMutation(
    $input: SetTxnsNeedsReviewInputObject!
  ) {
    setTxnsNeedsReview(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const txnCounterpartLinkMutation = graphql`
  mutation needsReviewPage_TxnCounterpartLinkMutation($input: TxnCounterpartLinkInputObject!) {
    txnCounterpartLink(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

type Row = needsReviewPage_Query["response"]["transactions"]["txns"][number];

const money2 = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

export default function NeedsReviewPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<needsReviewPage_Query>;
}) {
  const preloaded = usePreloadedQuery(needsReviewQuery, queryRef);
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(preloaded);
  useEffect(() => setData(preloaded), [preloaded]);

  // Sort runs server-side (so a page is globally sorted, not just locally reordered);
  // the worklist is offset-paginated. The From/To/Other-side cells are custom columns
  // with no shell sort header, so they never participate in sorting.
  const { sort, direction, onSort } = useTxnSort("DATE", "DESC");
  const [page, setPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const fetchPage = () => {
    setIsFetching(true);
    fetchQuery<needsReviewPage_Query>(environment, needsReviewQuery, {
      perspectiveId: getPerspectiveId() ?? undefined,
      sort,
      sortDirection: direction,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .toPromise()
      .then((fresh) => fresh && setData(fresh))
      .finally(() => setIsFetching(false));
  };

  // Refetch on sort / page change (skip first render — the loader has page 0).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, direction, page]);

  const handleSort = (column: TxnSortColumn) => {
    onSort(column);
    setPage(0);
  };
  // After an account or stakeholder assignment, the proposed rule to confirm — an
  // internal-link rule (-> account) or an attribution rule (-> stakeholder).
  const [ruleProposal, setRuleProposal] = useState<{
    regex: string;
    matchKind: string;
    totalCount: number;
    verb: string; // "Link" / "Attribute"
    targetLabel: string; // account or stakeholder name
    // Destination filter is only meaningful for attribution rules; an internal-link
    // rule's destination is its target (other_bank_account_id is NULL at match time).
    showDestination: boolean;
    prefillSourceIds: string[];
    ruleInput:
      | { targetType: "INTERNAL_LINK"; destinationBankAccountId: string }
      | { targetType: "STAKEHOLDER"; stakeholderId: string };
  } | null>(null);

  const rows = data.transactions.txns;
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);

  // Account nickname by full number — the hover label for a row's known (own) end.
  const accountNameByNumber = useMemo(
    () => new Map(data.bankAccounts.map((a) => [a.number, a.nickname || a.name])),
    [data.bankAccounts]
  );

  const refresh = () => fetchPage();

  // The distinct bank-account ids the given txns sit on (their source accounts),
  // for the rule filter's one-click "use selection's accounts" prefill.
  const sourceIdsForTxns = (txnIds: ReadonlyArray<string>): string[] => {
    const ids = new Set(txnIds);
    const numbers = new Set(
      rows.filter((r) => ids.has(r.txn.id)).map((r) => r.accountNumber).filter(Boolean)
    );
    return data.bankAccounts.filter((a) => numbers.has(a.number)).map((a) => a.id);
  };

  const afterMutation = (clearSelection?: () => void) => {
    clearSelection?.();
    setBusy(false);
    refresh();
  };

  // Resolve as internal: link txns to an owned account (existing or created on the
  // fly). markTxnsInternal makes them structurally internal, so they drop off the list.
  const assignAccount = (
    txnIds: ReadonlyArray<string>,
    choice: { bankAccountId?: string; name?: string },
    clearSelection?: () => void
  ) => {
    setBusy(true);
    commitMutation<needsReviewPage_MarkTxnsInternalMutation>(environment, {
      mutation: markTxnsInternalMutation,
      variables: {
        input: {
          txnIds: [...txnIds],
          destinationBankAccountId: choice.bankAccountId,
          destinationName: choice.name,
        },
      },
      onCompleted: (res) => {
        const payload = res.markTxnsInternal;
        if (payload?.errors?.length) {
          setBusy(false);
          notify({ message: payload.errors[0].message, kind: "error" });
          return;
        }
        notify({ message: `Linked ${txnIds.length} as internal.`, kind: "success" });
        const suggestion = payload?.suggestion;
        const destination = payload?.destination;
        afterMutation(clearSelection);
        // Offer to generalize into a standing rule when the user picked a pattern (2+
        // selected) or the pattern catches more than the selection.
        if (suggestion && destination && (txnIds.length >= 2 || suggestion.additionalCount > 0)) {
          setRuleProposal({
            regex: suggestion.regex,
            matchKind: suggestion.matchKind,
            totalCount: suggestion.totalCount,
            verb: "Link",
            targetLabel: destination.nickname || destination.name,
            showDestination: false, // internal-link destination IS the target
            prefillSourceIds: sourceIdsForTxns(txnIds),
            ruleInput: { targetType: "INTERNAL_LINK", destinationBankAccountId: destination.id },
          });
        }
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not link the account — please try again.", kind: "error" });
      },
    });
  };

  // Confirm the proposed rule: apply it to ALL matching txns (link to the account, or
  // attribute to the stakeholder) — now and on future imports.
  const confirmRule = (regex: string, filter: RuleAccountFilterValue) => {
    if (!ruleProposal) return;
    setBusy(true);
    commitMutation<needsReviewPage_CreateRuleMutation>(environment, {
      mutation: createRuleMutation,
      variables: {
        input: {
          ...ruleProposal.ruleInput,
          ...ruleFilterInput(filter),
          regex,
          matchKind: ruleProposal.matchKind,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: (res) => {
        setBusy(false);
        setRuleProposal(null);
        const payload = res.createRule;
        if (payload?.errors?.length) {
          notify({ message: payload.errors[0].message, kind: "error" });
          return;
        }
        notify({
          message: `Rule created — linked ${payload?.rule?.matchesCount ?? 0} matching transactions.`,
          kind: "success",
        });
        refresh();
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not create the rule — please try again.", kind: "error" });
      },
    });
  };

  // Resolve as external: attribute txns to a real counterparty. This clears the manual
  // worklist flag and gives them a real counterparty, so they drop off the list.
  const assignStakeholder = (
    txnIds: ReadonlyArray<string>,
    choice: { stakeholderId?: string; name?: string },
    clearSelection?: () => void
  ) => {
    setBusy(true);
    commitMutation<needsReviewPage_AttributeTxnsMutation>(environment, {
      mutation: attributeTxnsMutation,
      variables: {
        input: {
          txnIds: [...txnIds],
          stakeholderId: choice.stakeholderId,
          name: choice.name,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: (res) => {
        const payload = res.attributeTxns;
        if (payload?.errors?.length) {
          setBusy(false);
          notify({ message: payload.errors[0].message, kind: "error" });
          return;
        }
        notify({ message: `Attributed ${txnIds.length} transaction(s).`, kind: "success" });
        const suggestion = payload?.suggestion;
        const stakeholder = payload?.stakeholder;
        afterMutation(clearSelection);
        // Offer to generalize into a standing rule when the user picked a pattern (2+
        // selected) or the pattern catches more than the selection.
        if (suggestion && stakeholder?.id && (txnIds.length >= 2 || suggestion.additionalCount > 0)) {
          setRuleProposal({
            regex: suggestion.regex,
            matchKind: suggestion.matchKind,
            totalCount: suggestion.totalCount,
            verb: "Attribute",
            targetLabel: stakeholder.name,
            showDestination: true,
            prefillSourceIds: sourceIdsForTxns(txnIds),
            ruleInput: { targetType: "STAKEHOLDER", stakeholderId: stakeholder.id },
          });
        }
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not attribute — please try again.", kind: "error" });
      },
    });
  };

  // Remove manually-flagged rows from the worklist (clear op_needs_review_manual).
  const removeFromWorklist = (txnIds: ReadonlyArray<string>, clearSelection?: () => void) => {
    setBusy(true);
    commitMutation<needsReviewPage_SetTxnsNeedsReviewMutation>(environment, {
      mutation: setNeedsReviewMutation,
      variables: { input: { txnIds: [...txnIds], needsReview: false } },
      onCompleted: () => {
        notify({ message: "Removed from the worklist.", kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not remove — please try again.", kind: "error" });
      },
    });
  };

  // Resolve as internal by binding a specific opposite leg the user picked ("this is
  // the other side"). Both legs go internal and drop off the list. A 0-count result
  // means the chosen leg was just linked elsewhere — refresh so the stale candidate clears.
  const linkCounterpart = (txnId: string, counterpartTxnId: string) => {
    setBusy(true);
    commitMutation<needsReviewPage_TxnCounterpartLinkMutation>(environment, {
      mutation: txnCounterpartLinkMutation,
      variables: { input: { txnId, counterpartTxnId } },
      onCompleted: (res) => {
        const payload = res.txnCounterpartLink;
        if (payload?.errors?.length) {
          setBusy(false);
          notify({ message: payload.errors[0].message, kind: "error" });
          refresh();
          return;
        }
        if ((payload?.updatedCount ?? 0) === 0) {
          setBusy(false);
          notify({ message: "That match was just linked elsewhere — refreshed.", kind: "error" });
          refresh();
          return;
        }
        notify({ message: "Linked the pair as internal.", kind: "success" });
        afterMutation();
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not link — please try again.", kind: "error" });
      },
    });
  };

  // The From / To transfer-orientation cells. A needs-review transfer has exactly one
  // known end — the account it sits on — and one unconfirmed end (the "?" you assign).
  // An outflow leaves this account (From = here, To = ?); an inflow lands here.
  const orientationCell = (row: Row, end: "from" | "to") => {
    const ownLast4 = row.accountNumber ? row.accountNumber.slice(-4) : null;
    const ownAccountName = row.accountNumber
      ? accountNameByNumber.get(row.accountNumber) ?? undefined
      : undefined;
    const knownSide = (
      <span className="text-gray-700 tabular-nums" title={ownAccountName}>
        {ownLast4 ? `••${ownLast4}` : "—"}
      </span>
    );
    const unknownSide = (
      <span
        className="text-amber-600 font-semibold"
        title="Unconfirmed — assign the account on this end"
      >
        ?
      </span>
    );
    const outflow = row.amount < 0;
    const showKnown = end === "from" ? outflow : !outflow;
    return showKnown ? knownSide : unknownSide;
  };

  const exportParams: Record<string, string> = {
    needs_review: "true",
    sort: sort,
    sort_direction: direction,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold">Needs review</h1>
          {isFetching && (
            <span
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
              role="status"
              aria-label="Loading"
            />
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Transfer-looking transactions whose internal counterparty isn't confirmed.
          They're held out of revenue and cost until you assign the owned account on
          the other end (or attribute a real counterparty). Until then their cash sits
          in Suspense.
        </p>
      </div>

      <UnprojectedDecisions
        decisions={data.unprojectedDecisions ?? []}
        onChanged={refresh}
      />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <TxnTablePanel<Row>
          mode="page"
          interaction="editable"
          title="Worklist"
          totalCount={data.transactions.totalCount}
          rows={rows}
          emptyLabel="Nothing to review — every transfer's counterparty account is confirmed. 🎉"
          sort={sort}
          direction={direction}
          onSort={handleSort}
          pagination={{ page, pageSize: PAGE_SIZE, onPageChange: setPage }}
          exportParams={exportParams}
          busy={busy}
          hideColumns={["ACCOUNT", "COUNTERPARTY", "CATEGORY"]}
          extraColumns={[
            {
              key: "from",
              header: "From",
              render: (row) => orientationCell(row, "from"),
            },
            {
              key: "to",
              header: "To",
              render: (row) => orientationCell(row, "to"),
            },
            {
              key: "actions",
              header: "Other side",
              render: (row) => (
                <OtherSideCell
                  row={row}
                  accountOptions={accountOptions}
                  busy={busy}
                  onAssignAccount={(choice) => assignAccount([row.txn.id], choice)}
                  onLinkCounterpart={(counterpartTxnId) =>
                    linkCounterpart(row.txn.id, counterpartTxnId)
                  }
                  onRemove={() => removeFromWorklist([row.txn.id])}
                />
              ),
            },
          ]}
          provenance={(row) =>
            row.needsReviewManual ? (
              <span className="text-amber-600">· sent manually</span>
            ) : null
          }
          renderBulkActions={(selectedIds, clearSelection) => {
            const selectedManual =
              selectedIds.length > 0 &&
              rows
                .filter((r) => selectedIds.includes(r.txn.id))
                .every((r) => r.needsReviewManual);
            return (
              <>
                <BulkAction label="Assign account" disabled={busy}>
                  {(close) => (
                    <AccountCombobox
                      options={accountOptions}
                      placeholder="Account on the other end…"
                      alwaysShowCreate
                      onPick={(choice) => {
                        close();
                        assignAccount(selectedIds, choice, clearSelection);
                      }}
                    />
                  )}
                </BulkAction>
                <BulkAction label="Assign stakeholder" disabled={busy}>
                  {(close) => (
                    <StakeholderCombobox
                      options={stakeholderOptions}
                      onPick={(choice) => {
                        close();
                        assignStakeholder(selectedIds, choice, clearSelection);
                      }}
                    />
                  )}
                </BulkAction>
                {selectedManual && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => removeFromWorklist(selectedIds, clearSelection)}
                    className="px-2 py-1 rounded bg-white text-gray-900 hover:bg-gray-100 text-xs disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </>
            );
          }}
        />
      </div>

      {ruleProposal && (
        <RuleProposalDialog
          proposal={ruleProposal}
          accounts={accountOptions}
          busy={busy}
          onCreate={confirmRule}
          onClose={() => setRuleProposal(null)}
        />
      )}
    </div>
  );
}

// After assigning an account, confirm turning the one-off link into a standing rule
// that links every matching transaction to the same account. The pattern is editable,
// and the rule can optionally be limited to specific source/destination accounts.
function RuleProposalDialog({
  proposal,
  accounts,
  busy,
  onCreate,
  onClose,
}: {
  proposal: {
    regex: string;
    matchKind: string;
    totalCount: number;
    verb: string;
    targetLabel: string;
    showDestination: boolean;
    prefillSourceIds: string[];
  };
  accounts: ReadonlyArray<AccountOption>;
  busy: boolean;
  onCreate: (regex: string, filter: RuleAccountFilterValue) => void;
  onClose: () => void;
}) {
  const [regex, setRegex] = useState(proposal.regex);
  const [filter, setFilter] = useState<RuleAccountFilterValue>(
    EMPTY_RULE_ACCOUNT_FILTER
  );
  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[480px] p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800">
          Make this a rule?
        </h2>
        <p className="text-sm text-gray-600">
          {proposal.verb} every transaction matching this pattern to{" "}
          <span className="font-medium">{proposal.targetLabel}</span> — now and on
          future imports. About {proposal.totalCount.toLocaleString()} transaction
          {proposal.totalCount === 1 ? "" : "s"} match.
        </p>
        <div className="space-y-1">
          <label className="text-xs text-gray-500">
            Match {proposal.matchKind.toLowerCase()}
          </label>
          <input
            value={regex}
            onChange={(e) => setRegex(e.target.value)}
            className="w-full rounded border px-2 py-1.5 font-mono text-xs"
          />
        </div>
        <RuleAccountFilter
          accounts={accounts}
          value={filter}
          onChange={setFilter}
          showDestination={proposal.showDestination}
          prefillSourceIds={proposal.prefillSourceIds}
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
          >
            Not now
          </button>
          <button
            type="button"
            disabled={busy || !regex.trim()}
            onClick={() => onCreate(regex.trim(), filter)}
            className="px-3 py-1.5 text-sm rounded bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
          >
            Create rule
          </button>
        </div>
      </div>
    </div>
  );
}

// The per-row counterpart finder + assign/remove affordances, rendered in the "Other
// side" extra column. One-click bind to the top fuzzy candidate (txnCounterpartLink),
// a "see N others" popover for the rest, an account picker, and (for manual rows) Remove.
function OtherSideCell({
  row,
  accountOptions,
  busy,
  onAssignAccount,
  onLinkCounterpart,
  onRemove,
}: {
  row: Row;
  accountOptions: ReadonlyArray<AccountOption>;
  busy: boolean;
  onAssignAccount: (choice: { bankAccountId?: string; name?: string }) => void;
  onLinkCounterpart: (counterpartTxnId: string) => void;
  onRemove: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [linking, setLinking] = useState(false);
  // Reset the in-flight label when the page-level mutation settles (success unmounts
  // this row via refresh; an error flips busy back off and we clear the label here).
  useEffect(() => {
    if (!busy) setLinking(false);
  }, [busy]);

  const candidates = row.counterpartCandidates;
  const top = candidates[0];
  const rest = candidates.slice(1);
  const acctLabel = (c: (typeof candidates)[number]) =>
    c.bankAccount.last4 ? `${c.bankAccount.nickname} ••${c.bankAccount.last4}` : c.bankAccount.nickname;
  const link = (counterpartTxnId: string) => {
    setExpanded(false);
    setLinking(true);
    onLinkCounterpart(counterpartTxnId);
  };

  return (
    <div className="flex items-center justify-end gap-2 flex-wrap">
      {top && (
        <div className="relative">
          <button
            type="button"
            disabled={busy}
            onClick={() => link(top.txn.id)}
            title={`This is the other side: ${acctLabel(top)} · ${money2(top.amount)} · ${top.postingDate.slice(0, 10)}`}
            className="px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 text-xs disabled:opacity-50 whitespace-nowrap"
          >
            {linking
              ? "Linking…"
              : `Other side ↔ ••${top.bankAccount.last4 ?? "?"} ${money2(top.amount)}`}
          </button>
          {rest.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="ml-1 text-xs text-gray-500 underline hover:no-underline"
            >
              see {rest.length} other{rest.length === 1 ? "" : "s"}
              {row.counterpartCandidateCount > candidates.length
                ? ` (${row.counterpartCandidateCount} total)`
                : ""}
            </button>
          )}
          {expanded && rest.length > 0 && (
            <div className="absolute right-0 z-50 mt-1 w-72 rounded border bg-white p-2 shadow-lg text-gray-700 space-y-1">
              {rest.map((c) => (
                <div key={c.txn.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate">
                    {acctLabel(c)} · {money2(c.amount)} · {c.postingDate.slice(0, 10)}
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => link(c.txn.id)}
                    className="shrink-0 px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    this is it
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          disabled={busy}
          onClick={() => setPickerOpen((open) => !open)}
          className="px-2 py-1 rounded bg-gray-900 text-white hover:bg-gray-700 text-xs disabled:opacity-50"
        >
          Assign account
        </button>
        {pickerOpen && (
          <div className="absolute right-0 z-50 mt-1 rounded border bg-white p-2 shadow-lg text-gray-700">
            <AccountCombobox
              options={accountOptions}
              placeholder="The account on the other end…"
              alwaysShowCreate
              onPick={(choice) => {
                setPickerOpen(false);
                onAssignAccount(choice);
              }}
            />
          </div>
        )}
      </div>

      {row.needsReviewManual && (
        <button
          type="button"
          disabled={busy}
          onClick={onRemove}
          className="text-xs text-gray-500 underline hover:no-underline disabled:opacity-50"
        >
          Remove
        </button>
      )}
    </div>
  );
}
